import React, { useState, useEffect } from 'react';
import { Users, Trash2, RefreshCw, AlertCircle } from 'lucide-react';
import { getAllUsersAsAdmin, deleteUserFromFirestore } from '../firebase/admin';
import { getDefaultAvatar } from '../firebase/storage';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [resettingAvatars, setResettingAvatars] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const result = await getAllUsersAsAdmin();
    if (result.success) {
      setUsers(result.users);
    } else {
      showMessage(`❌ Error loading users: ${result.error}`, 'error');
    }
    setLoading(false);
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This will remove them from Firestore.`)) {
      return;
    }

    const result = await deleteUserFromFirestore(userId);
    if (result.success) {
      showMessage(`✅ Successfully deleted ${userName}`);
      loadUsers();
    } else {
      showMessage(`❌ Error deleting user: ${result.error}`, 'error');
    }
  };

  const handleResetAllAvatars = async () => {
    if (!window.confirm('Are you sure you want to reset ALL user profile pictures to default avatars? This cannot be undone.')) {
      return;
    }

    setResettingAvatars(true);
    showMessage('⏳ Resetting avatars...', 'info');

    try {
      const updates = users.map(async (user) => {
        const defaultAvatar = getDefaultAvatar(user.name || 'User');
        await updateDoc(doc(db, 'users', user.id), {
          avatar: defaultAvatar
        });
      });

      await Promise.all(updates);
      showMessage(`✅ Successfully reset ${users.length} user avatars!`);
      loadUsers();
    } catch (error) {
      showMessage(`❌ Error resetting avatars: ${error.message}`, 'error');
    }

    setResettingAvatars(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-red-600 mx-auto mb-3" />
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">Total users: {users.length}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadUsers}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={handleResetAllAvatars}
            disabled={resettingAvatars || users.length === 0}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {resettingAvatars ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Resetting...</span>
              </>
            ) : (
              <>
                <Users className="w-4 h-4" />
                <span>Reset All Avatars</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('❌') 
            ? 'bg-red-100 text-red-800 border border-red-200' 
            : message.includes('⏳')
            ? 'bg-blue-100 text-blue-800 border border-blue-200'
            : 'bg-green-100 text-green-800 border border-green-200'
        }`}>
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5" />
            <span>{message}</span>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {users.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
            <p className="text-gray-600">Users will appear here once they sign up</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <img
                          src={user.avatar || getDefaultAvatar(user.name || 'User')}
                          alt={user.name}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            e.target.src = getDefaultAvatar(user.name || 'User');
                          }}
                        />
                        <div>
                          <div className="font-medium text-gray-900">{user.name || 'Unknown'}</div>
                          <div className="text-sm text-gray-500">ID: {user.id.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.location || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {user.createdAt ? new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
